import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import {
  adminUpdateCompany,
  updateCompany as updateCompanyApi,
} from "../api/companies.api";
import { UpdateCompanyPayload } from "../api/models/companies.models";

const useUpdateCompany = ({ isAdmin }: { isAdmin: boolean }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const updateCompany = async (payload: UpdateCompanyPayload) => {
    return isAdmin
      ? await adminUpdateCompanyMutation.mutateAsync(payload)
      : await userUpdateCompanyMutation.mutateAsync(payload);
  };

  const adminUpdateCompanyMutation = useMutation({
    mutationFn: (payload: UpdateCompanyPayload) =>
      adminUpdateCompany(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update company:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-company-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-company-updated"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["adminCompanies", true] });
    },
  });

  const userUpdateCompanyMutation = useMutation({
    mutationFn: (payload: UpdateCompanyPayload) =>
      updateCompanyApi(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update company:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-company-update-failed"),
        life: 3000,
      });
    },
    onSuccess: (_data, variables) => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-company-updated"),
        life: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: ["companies", variables.id],
      });
    },
  });

  const isUpdatingCompany =
    adminUpdateCompanyMutation.isPending || userUpdateCompanyMutation.isPending;

  return {
    updateCompany,
    isUpdatingCompany,
  };
};

export default useUpdateCompany;
